/**
 * Robust extractor for axios error → user-facing string.
 *
 * Handles all error envelopes the Dispatcher API returns:
 *
 * 1. Standard errorsPlugin wrap (most routes use throwBadRequest /
 *    throwUnauthorized / etc):
 *      { error: { code: 'ERR_VALIDATION', message: '…',
 *                 request_id: '…', issues?: [...] } }
 *
 * 2. Flat shape used by some LLM routes that handle errors inline:
 *      { error: 'ai_not_configured', detail: 'openai api_key missing' }
 *
 * 3. Older routes returning { error: '…' } as a plain string.
 *
 * Returns a plain string suitable for React rendering. Never returns an
 * object — that was the cause of "Minified React error #31" crashes
 * (object with keys {code, message, request_id} was passed as a child).
 *
 * Optional `codeMap` lets a caller customise messages for known error
 * codes (e.g. 'ai_not_configured' → "L'IA n'est pas configurée").
 */

import axios from 'axios';

interface NestedError {
  code?: string;
  message?: string;
  request_id?: string;
  detail?: string;
  issues?: unknown;
  [k: string]: unknown;
}

interface ApiErrorBody {
  error?: string | NestedError;
  detail?: string;
  message?: string;
}

export type ErrorCodeMap = Record<string, string>;

interface ZodIssue {
  path?: unknown[];
  message?: string;
  code?: string;
}

function summarizeIssues(issues: unknown): string {
  if (!Array.isArray(issues) || issues.length === 0) return '';
  const formatted = (issues as ZodIssue[])
    .slice(0, 4)
    .map((iss) => {
      const path = Array.isArray(iss.path) && iss.path.length > 0 ? iss.path.join('.') : '(root)';
      return `${path}: ${iss.message ?? iss.code ?? 'invalid'}`;
    });
  const more = issues.length > 4 ? ` (+${issues.length - 4} autres)` : '';
  return `[${formatted.join(' • ')}${more}]`;
}

export function getApiErrorMessage(err: unknown, codeMap: ErrorCodeMap = {}): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as ApiErrorBody | undefined;
    const status = err.response?.status;

    // Shape 1: { error: { code, message, ... } } — standard envelope.
    if (data?.error && typeof data.error === 'object') {
      const nested = data.error;
      const baseMsg =
        (nested.code && codeMap[nested.code]) ||
        nested.message ||
        nested.detail ||
        `Erreur (HTTP ${status ?? '?'})`;
      const issuesSummary = summarizeIssues(nested.issues);
      return issuesSummary ? `${baseMsg} ${issuesSummary}` : baseMsg;
    }

    // Shape 2 / 3: { error: '…' } string.
    if (typeof data?.error === 'string') {
      if (codeMap[data.error]) return codeMap[data.error]!;
      if (data.detail) return `${data.error} — ${data.detail}`;
      return data.error;
    }

    if (data?.detail) return data.detail;
    if (data?.message) return data.message;
    return `Erreur (HTTP ${status ?? '?'})`;
  }

  if (err instanceof Error) return err.message;
  return String(err);
}
